import clsx from 'clsx';
import React, { useRef, useState } from 'react';

export type SuggestionMenuItem<T = unknown> = {
  id: string;
} & T;

interface SuggestionMenuProps<T> {
  items: SuggestionMenuItem<T>[];
  onSelect: (item: SuggestionMenuItem<T>) => void;
  renderItem: (
    item: SuggestionMenuItem<T>,
    isFocused: boolean,
  ) => React.ReactNode;
}

export const SuggestionMenu = <T,>({
  items,
  onSelect,
  renderItem,
}: SuggestionMenuProps<T>): React.ReactElement | null => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset focused index when items change
  React.useEffect(() => {
    setFocusedIndex(0);
  }, [items]);

  // Scroll focused item into view
  React.useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      const focusedElement = listElement.children[focusedIndex];
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
      }
      if (event.key === 'Enter') {
        const selectedItem = items[focusedIndex];
        if (selectedItem) {
          event.preventDefault();
          event.stopPropagation();
          onSelect(selectedItem);
        }
        // Let Enter propagate if no item is selected
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [focusedIndex, items, onSelect]);

  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      ref={listRef}
      className="flex flex-col list-none bg-white border rounded shadow-popover-050 max-h-[200px] overflow-y-auto"
      role="menu"
    >
      {items.map((item, index) => (
        <li
          key={item.id}
          className={clsx(
            'flex flex-row items-center px-4 py-2 gap-3 cursor-pointer hover:bg-primary-hover rounded',
            focusedIndex === index && 'bg-primary-hover',
          )}
          onClick={() => onSelect(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSelect(item);
            }
          }}
          tabIndex={index}
          role="menuitem"
        >
          {renderItem(item, focusedIndex === index)}
        </li>
      ))}
    </ul>
  );
};
